'use client';

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Loader2, X, BookOpen, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'react-hot-toast';

interface ProposalStep {
  step_number: number;
  description: string;
  tool: string;
  args: Record<string, unknown>;
}

interface ProposalCardType {
  approval_token: string;
  diagnosis: string;
  root_cause: string;
  confidence: string;
  steps: ProposalStep[];
  risk_level: string;
  automated_fix_possible: boolean;
  dispatcher_note?: string;
  matched_policy_id?: number;
  matched_policy_name?: string;
}

interface ProposalCardProps {
  proposal: ProposalCardType;
  conversationId: string;
  chatHistory: Array<Record<string, unknown>>;
  language: string;
  onApproved: (result: string) => void;
  onCancelled: () => void;
  onEdit?: (originalQuery: string) => void;
}

export default function ProposalCard({
  proposal,
  conversationId,
  chatHistory,
  language,
  onApproved,
  onCancelled,
  onEdit,
}: ProposalCardProps) {
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Save as AI Policy state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);
  const [policyTitle, setPolicyTitle] = useState('');
  const [policyDescription, setPolicyDescription] = useState('');
  const [alertType, setAlertType] = useState('');
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);

  const getRiskBadgeColor = (risk: string) => {
    switch (risk.toLowerCase()) {
      case 'low':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'medium':
        return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'high':
        return 'bg-red-50 border-red-200 text-red-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const getConfidenceBadgeColor = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case 'high':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'medium':
        return 'bg-purple-50 border-purple-200 text-purple-700';
      case 'low':
        return 'bg-gray-50 border-gray-200 text-gray-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const handleApprove = async () => {
    setIsExecuting(true);
    setCurrentStep(0);

    try {
      // Call the approve endpoint
      const response = await fetch('/api/chatbot/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          approval_token: proposal.approval_token,
          conversationId,
          chatHistory,
          language,
        }),
      });

      if (!response.ok) {
        throw new Error(`Approval failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Call onApproved with the response text
      onApproved(data.response || 'Actions completed successfully.');
    } catch (error) {
      console.error('Error executing proposal:', error);
      onApproved(
        `⚠️ An error occurred while executing the plan: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsExecuting(false);
      setCurrentStep(0);
    }
  };

  const handleExplain = () => {
    // Send a new message asking for explanation
    if (onEdit) {
      onEdit(`Explain why you proposed: ${proposal.root_cause}`);
    }
  };

  const handleCancel = () => {
    onCancelled();
  };

  const handleSaveAsPolicy = async () => {
    setShowSaveDialog(true);
    setAlertType(''); // User needs to specify alert type
    setPolicyDescription(proposal.root_cause || '');
    
    // Try to generate a title from the diagnosis
    if (proposal.diagnosis) {
      setIsGeneratingTitle(true);
      try {
        const response = await fetch('/api/ai-policies/generate-title', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            alert_type: 'general_incident',
            description: proposal.diagnosis
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setPolicyTitle(data.name || 'Incident Response Policy');
          setPolicyDescription(data.description || proposal.root_cause || '');
        } else {
          setPolicyTitle('Incident Response Policy');
        }
      } catch (error) {
        console.error('Error generating title:', error);
        setPolicyTitle('Incident Response Policy');
      } finally {
        setIsGeneratingTitle(false);
      }
    } else {
      setPolicyTitle('Incident Response Policy');
    }
  };

  const handleConfirmSavePolicy = async () => {
    if (!policyTitle.trim() || !alertType.trim()) {
      toast.error('Policy name and alert type are required');
      return;
    }

    setIsSavingPolicy(true);
    const toastId = toast.loading('Saving AI policy...');
    
    try {
      // Convert ProposalStep[] to PolicyStep[] format
      const writeKeywords = ['isolate', 'block', 'reboot', 'disable', 'change', 'apply', 'update', 'throttle', 'unblock', 'blink', 'cycle'];
      
      const policySteps = proposal.steps.map((step, index) => {
        const toolName = step.tool.toLowerCase();
        const isWriteAction = writeKeywords.some(keyword => toolName.includes(keyword));
        
        return {
          step_number: index + 1,
          step_type: isWriteAction ? 'EXECUTE_ACTION' : 'EXECUTE_TOOL',
          details: {
            tool_name: step.tool,
            parameters: step.args
          }
        };
      });

      const response = await fetch('/api/ai-policies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: policyTitle,
          description: policyDescription,
          trigger_condition: { alert_type: alertType },
          steps: policySteps
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save policy');
      }

      toast.success('Policy saved successfully!', { id: toastId });
      setShowSaveDialog(false);
      setPolicyTitle('');
      setPolicyDescription('');
      setAlertType('');
    } catch (error: unknown) {
      let errorMessage = 'Unknown error';
      if (error instanceof Error) errorMessage = error.message;
      else if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: string }).message === 'string') {
        errorMessage = (error as { message: string }).message;
      }
      if (errorMessage.includes('already have a policy')) {
        errorMessage = 'A policy with this name already exists. Please choose a different name.';
      }
      toast.error(`Failed to save policy: ${errorMessage}`, { id: toastId });
    } finally {
      setIsSavingPolicy(false);
    }
  };

  const handleCancelSavePolicy = () => {
    setShowSaveDialog(false);
    setPolicyTitle('');
    setPolicyDescription('');
    setAlertType('');
  };

  return (
    <Card className="mt-3 bg-white rounded-[16px] border border-[#EAEAEA] shadow-sm">
      <CardContent className="p-[20px]">
        {/* Header with icon and confidence badge */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <h3 className="font-semibold text-[16px] text-[#141A42]">
                Remediation Plan
              </h3>
              <Badge className={`${getConfidenceBadgeColor(proposal.confidence)} border`}>
                {proposal.confidence} Confidence
              </Badge>
              <Badge className={`${getRiskBadgeColor(proposal.risk_level)} border`}>
                {proposal.risk_level.toUpperCase()} Risk
              </Badge>
            </div>
            {/* Matched policy indicator */}
            {proposal.matched_policy_name && (
              <div className="flex items-center gap-1.5 text-[13px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5 w-fit">
                <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Using AI Policy: <strong>{proposal.matched_policy_name}</strong></span>
              </div>
            )}
          </div>
        </div>

        {/* Root cause highlight */}
        <div className="bg-[#F5F7FA] rounded-lg p-3 mb-4">
          <p className="text-[14px] font-medium text-[#535EA4] mb-1">Root Cause:</p>
          <p className="text-[14px] text-[#141A42]">{proposal.root_cause}</p>
        </div>

        {/* Steps list */}
        <div className="mb-4">
          <p className="text-[14px] font-medium text-[#535EA4] mb-2">Proposed Actions:</p>
          <div className="space-y-2">
            {proposal.steps.map((step) => (
              <div
                key={step.step_number}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  isExecuting && currentStep === step.step_number
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-white border-[#EAEAEA]'
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {isExecuting && currentStep === step.step_number ? (
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  ) : isExecuting && currentStep > step.step_number ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#535EA4] text-white flex items-center justify-center text-[12px] font-medium">
                      {step.step_number}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-[14px] text-[#141A42]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dispatcher note if present */}
        {proposal.dispatcher_note && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-[13px] text-amber-800">
              <strong>Note:</strong> {proposal.dispatcher_note}
            </p>
          </div>
        )}

        {/* Save as AI Policy Dialog */}
        {showSaveDialog && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 space-y-4 mb-4">
            <div className="flex items-center justify-between">
              <h4 className="text-[15px] font-semibold text-blue-900 flex items-center gap-2">
                <Save className="h-4 w-4" />
                Save as AI Policy
              </h4>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleCancelSavePolicy}
                className="h-7 w-7 p-0"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            
            <div className="space-y-3">
              <div>
                <Label className="text-[13px] font-medium text-blue-900">Alert Type *</Label>
                <Input
                  value={alertType}
                  onChange={(e) => setAlertType(e.target.value)}
                  placeholder="e.g., malware_detected, device_offline"
                  className="mt-1 text-[13px]"
                />
                <p className="text-[11px] text-blue-700 mt-1">
                  This policy will trigger for alerts matching this type
                </p>
              </div>
              
              <div>
                <Label className="text-[13px] font-medium text-blue-900">Policy Name *</Label>
                {isGeneratingTitle ? (
                  <div className="flex items-center gap-2 p-2 text-[13px] text-blue-700">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Generating title...
                  </div>
                ) : (
                  <Input
                    value={policyTitle}
                    onChange={(e) => setPolicyTitle(e.target.value)}
                    placeholder="Enter policy name"
                    className="mt-1 text-[13px]"
                  />
                )}
              </div>
              
              <div>
                <Label className="text-[13px] font-medium text-blue-900">Description</Label>
                <Textarea
                  value={policyDescription}
                  onChange={(e) => setPolicyDescription(e.target.value)}
                  placeholder="Brief description of what this policy does"
                  rows={2}
                  className="mt-1 text-[13px]"
                />
              </div>

              <Alert className="bg-white border-blue-200">
                <AlertDescription className="text-[11px] text-blue-800">
                  This will create a reusable AI policy with {proposal.steps.length} steps that can automatically respond to similar incidents.
                </AlertDescription>
              </Alert>
              
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={handleCancelSavePolicy}
                  disabled={isSavingPolicy}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmSavePolicy}
                  disabled={!policyTitle.trim() || !alertType.trim() || isSavingPolicy || isGeneratingTitle}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  {isSavingPolicy ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-3.5 w-3.5" />
                      Save Policy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Button
            onClick={handleApprove}
            disabled={isExecuting || !proposal.automated_fix_possible}
            className="bg-[#535EA4] hover:bg-[#4A5293] text-white"
          >
            {isExecuting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Executing...
              </>
            ) : (
              'Confirm & Fix'
            )}
          </Button>

          <Button
            onClick={handleExplain}
            disabled={isExecuting}
            variant="outline"
            className="border-[#535EA4] text-[#535EA4] hover:bg-[#535EA4] hover:text-white"
          >
            Explain Why
          </Button>

          {!proposal.matched_policy_id && (
            <Button
              onClick={handleSaveAsPolicy}
              disabled={isExecuting || isSavingPolicy}
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              <Save className="w-4 h-4 mr-1" />
              Save as AI Policy
            </Button>
          )}

          <Button
            onClick={handleCancel}
            disabled={isExecuting}
            variant="outline"
            className="border-[#9F9C9C] text-[#9F9C9C] hover:bg-gray-100"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        </div>

        {/* Status message during execution */}
        {isExecuting && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-[13px] text-blue-800">
              Executing step {currentStep} of {proposal.steps.length}...
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
